<template>
  <micro-app
    v-if="state.appName && state.entry"
    class="micro-app-container"
    :name="state.appName"
    :url="state.entry"
    :baseroute="state.baseroute"
    @created="handleCreated"
    @mounted="handleMounted"
    @error="handleError"
    @afterhidden="afterhidden"
    @beforeshow="beforeshow"
    @aftershow="aftershow"
    disable-memory-router
    keep-router-state
    keep-alive
  ></micro-app>

  <div v-else class="no-config">
    <a-empty description="页面刷新中请稍后...">
      <template #image>
        <a-spin size="large" />
      </template>
    </a-empty>
  </div>
</template>

<script setup>
import { reactive, watch, computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import microApp from '@micro-zoe/micro-app'

import { getMFEMap, findMenuItem } from '../../utils/mfe'

// 缓存实例管理
const MAX_CACHE_INSTANCES = 10 // 最大缓存实例数
const cachedInstances = ref(new Set())

// 生成路由哈希值，用于创建唯一的micro-app实例
const generateRouteHash = routePath => {
  // 将路径转换为安全的标识符
  return (
    routePath
      .replace(/[^a-zA-Z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase() || 'root'
  )
}

// 管理缓存实例
const manageCacheInstances = appName => {
  if (cachedInstances.value.size >= MAX_CACHE_INSTANCES) {
    // 移除最旧的实例
    const oldestInstance = cachedInstances.value.values().next().value
    cachedInstances.value.delete(oldestInstance)

    // 销毁对应的micro-app实例
    if (window.microApp) {
      window.microApp.unmountApp(oldestInstance)
    }
  }

  cachedInstances.value.add(appName)
}

const route = useRoute()
const store = useStore()
const state = reactive({
  isLoading: false,
  appName: '',
  entry: '',
  baseroute: '',
  error: null
})

// 动态获取最新的微前端配置
const menuCodeHostMap = computed(() => getMFEMap() || {})

const handleCreated = () => {
  state.isLoading = true
  state.error = null
}

const handleMounted = () => {
  const activeApp = microApp.getActiveApps({ excludeHiddenApp: true })
  state.isLoading = false
}

const handleError = error => {
  console.error('微前端应用加载失败:', error)
  state.isLoading = false
  state.error = error
}

const afterhidden = e => {
  // const activeApp = microApp.getActiveApps({ excludeHiddenApp: true })
}

const beforeshow = e => {
  // const activeApp = microApp.getActiveApps({ excludeHiddenApp: true })
}

const aftershow = e => {
  // const activeApp = microApp.getActiveApps({ excludeHiddenApp: true })
}

watch(
  () => route.fullPath,
  () => {
    try {
      // 重置状态
      state.appName = ''
      state.entry = ''
      state.baseroute = ''
      state.error = null

      const pathList = route.fullPath.split('/').filter(String)

      // 验证路径有效性
      if (!pathList.length) {
        console.warn('无效的路由路径:', route.fullPath)
        return
      }

      const menuCode = pathList[0]
      const routePath =
        pathList.length > 1 ? '/' + pathList.slice(1).join('/') : '/'

      // 修正大小写转换方法
      const hostList = menuCodeHostMap.value[menuCode.toUpperCase()]

      if (menuCode && hostList?.length) {
        let [entry, ...hosts] = hostList
        const baseroute = `/${menuCode}/`

        // 如果有多个主机配置，尝试通过菜单项查找正确的入口
        if (hosts.length && routePath !== '/') {
          const menuItem = findMenuItem(store.state.user.menuList, routePath)
          if (menuItem?.origin) {
            entry = menuItem.origin
          }
        }

        // 验证 entry 的有效性
        if (!entry || typeof entry !== 'string') {
          console.warn('无效的微前端应用入口:', entry)
          return
        }

        // 生成唯一的应用名称，用于支持多路由缓存
        const appName = `${menuCode}-${generateRouteHash(routePath)}`
        // 管理缓存实例
        manageCacheInstances(appName)
        Object.assign(state, {
          appName,
          baseroute,
          entry: entry.startsWith('http') ? entry : `http://${entry}`
        })
      } else {
        console.warn('未找到微前端配置:', {
          menuCode,
          availableCodes: Object.keys(menuCodeHostMap.value)
        })
      }
    } catch (error) {
      console.error('路由解析失败:', error)
      state.error = error
    }
  },
  {
    immediate: true
  }
)
</script>
<style lang="scss">
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 80px);
  gap: 16px;

  .loading-text {
    color: #666;
    font-size: 14px;
  }
}

.error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 80px);
  padding: 20px;
}

.no-config {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 80px);
  padding: 20px;
}
</style>
